/** @jsxImportSource @emotion/react */
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { css } from '@emotion/react';
import { usePhotoFullSizeUri, usePhotoUriForLayoutSize } from '@kaiizen/kai-pro-api-react-state';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';

import { CheckIcon, CloseIcon, EditIcon, IconButton, LeftIcon, MissingPhotoIcon, ModalLayout, RightIcon, TrashIcon } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import type {
  PhotoDetailModel,
  PhotoDetailThumbnailModel,
} from './photoDetailModels';
import { DetailSection } from './PhotoDetailSections';

const MISSING_MEDIA_STYLE = css`
  label: CLOSEOUT_REVIEW_MISSING_PHOTO_MEDIA_STYLE;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  border-right: 1px solid ${COLORS.stroke};
  background-color: ${COLORS.strokeFaded};
  background-image: repeating-linear-gradient(
    135deg,
    rgba(165, 117, 83, 0.16) 0,
    rgba(165, 117, 83, 0.16) 18px,
    rgba(255, 255, 255, 0.38) 18px,
    rgba(255, 255, 255, 0.38) 36px
  );
`;

const MISSING_CARD_STYLE = css`
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px;
  border: 1px dashed ${COLORS.midtoneFaded};
  border-radius: 0;
  background: rgba(248, 245, 240, 0.86);
  color: ${COLORS.midtone};
  text-align: center;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
`;

const MISSING_ICON_STYLE = css`
  width: 54px;
  height: 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${COLORS.midtoneFaded};
  border-radius: 0;
  color: #a96947;
  font-size: ${pxToRem(24)};
`;

const MISSING_EYEBROW_STYLE = css`
  color: #a96947;
  font-size: ${pxToRem(11)};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const MISSING_TITLE_STYLE = css`
  color: ${COLORS.ink};
  font-size: ${pxToRem(18)};
  font-weight: 800;
  line-height: 1.333;
`;

const DETAIL_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 28px;
  overflow-y: auto;
  color: ${COLORS.ink};
`;

const DETAIL_HEADER_STYLE = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const DETAIL_TITLE_WRAP_STYLE = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DETAIL_TITLE_STYLE = css`
  margin: 0;
  color: var(--pd-pane-text, ${COLORS.ink});
  font-family: ${FONT.display};
  font-size: ${pxToRem(18)};
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.005em;
`;

const DETAIL_SUBTITLE_STYLE = css`
  color: var(--pd-subtitle, ${COLORS.midtoneFaded});
  font-size: ${pxToRem(13.5)};
  line-height: 1.333;
`;

const WARNING_BADGE_STYLE = css`
  align-self: flex-start;
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 0;
  background: ${COLORS.surfaceMuted};
  color: #8a6330;
  font-size: ${pxToRem(12)};
  font-weight: 800;
`;

const SECTION_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid ${COLORS.strokeFaded};
  border-radius: 0;
  background: ${COLORS.paper};
`;

const SECTION_TITLE_STYLE = css`
  color: var(--pd-muted, ${COLORS.midtone});
  font-size: ${pxToRem(12)};
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const BODY_TEXT_STYLE = css`
  color: var(--pd-pane-text, #4f4942);
  font-size: ${pxToRem(14)};
  line-height: 1.429;
`;

const PHOTO_MEDIA_STYLE = css`
  flex: var(--pd-hero-flex, 50);
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #000;
  border-right: 1px solid ${COLORS.stroke};
  color: #fff;
`;

const HERO_WRAP_STYLE = css`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  background: var(--pd-hero-bg, #908062);
`;

const HERO_STYLE = css`
  flex: 1;
  min-width: 0;
  background-color: var(--pd-hero-bg, #908062);
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
`;

const PHOTO_NAV_BUTTON_STYLE = css`
  position: absolute;
  top: 50%;
  z-index: 2;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 0;
  background: rgba(26, 26, 26, 0.72);
  color: #fff;
  font: inherit;
  font-size: ${pxToRem(34)};
  line-height: 1;
  transform: translateY(-50%);
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.24;
  }
`;

const PHOTO_NAV_PREVIOUS_STYLE = css`
  left: 14px;
`;

const PHOTO_NAV_NEXT_STYLE = css`
  right: 14px;
`;

const MEDIA_ROW_STYLE = css`
  min-width: 0;
  min-height: 0;
  display: flex;
  background: #000;
`;

const CAPTION_BAR_STYLE = css`
  flex: 0 0 auto;
  padding: 12px 20px;
  background: #0a0a0a;
  color: rgba(255, 255, 255, 0.85);
  font-size: ${pxToRem(13)};
`;

const RAIL_STYLE = css`
  flex: 15;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 14px;
  background: var(--pd-pane-bg, ${COLORS.white});
  border-left: 1px solid var(--pd-rule, ${COLORS.stroke});
  color: var(--pd-pane-text, ${COLORS.ink});
`;

const RAIL_SECTION_STYLE = css`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RAIL_TITLE_STYLE = css`
  flex-shrink: 0;
  margin: 0;
  font-size: ${pxToRem(13)};
  font-weight: 700;
  color: var(--pd-rail-title, ${COLORS.ink});
`;

const RAIL_COLUMN_STYLE = css`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding-right: 2px;

  /* Mockup .po-strip's own scrollbar. The default one is a heavy band in a column this narrow,
     and the pane it sits in may be dark, so the thumb follows the rail's rule colour. */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--pd-rule, rgba(0, 0, 0, 0.15));
  }
`;

const TASK_TOTAL_ROW_STYLE = css`
  display: flex;
  align-items: baseline;
  gap: 14px;
  padding: 4px 0 14px;
  border-bottom: 1px solid var(--pd-rule, ${COLORS.stroke});
`;

const TASK_TOTAL_LABEL_STYLE = css`
  font-size: ${pxToRem(13)};
  font-weight: 600;
  color: var(--pd-pane-text, ${COLORS.ink});
`;

const TASK_TOTAL_VALUE_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(14)};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--pd-pane-text, ${COLORS.ink});
`;

const GO_TO_EDITOR_STYLE = css`
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--pd-pane-text, ${COLORS.ink});
  font: inherit;
  font-size: ${pxToRem(12.5)};
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
  cursor: pointer;
`;

const NOTES_SECTION_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 0;
`;

const QUOTE_CARD_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: var(--pd-note-bg, ${COLORS.wash});
  border-left: 3px solid var(--pd-note-rule, ${COLORS.stroke});
`;

const QUOTE_TEXT_STYLE = css`
  margin: 0;
  font-size: ${pxToRem(14)};
  line-height: 1.55;
  color: var(--pd-pane-text, ${COLORS.ink});
`;

const QUOTE_CAPTION_STYLE = css`
  font-family: ${'Maxeville Construct, Maxeville, monospace'};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pd-muted, ${COLORS.midtoneFaded});
`;

const GROUP_PHOTO_NAV_STYLE = css`
  flex: 0 0 auto;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const DETAIL_PANEL_STYLE = css`
  position: relative;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--pd-pane-bg, ${COLORS.white});
  color: var(--pd-pane-text, ${COLORS.ink});
`;

const DETAIL_SCROLL_STYLE = css`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 16px 12px;
`;

const DETAIL_MOCKUP_HEADER_STYLE = css`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding-right: 44px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--pd-rule-soft, ${COLORS.strokeFaded});
`;

const DETAIL_LABEL_STYLE = css`
  color: var(--pd-muted, ${COLORS.midtoneFaded});
  font-size: ${pxToRem(14)};
`;

const ACTION_ROW_STYLE = css`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  padding: 12px 9px 14px;
  border-top: 1px solid var(--pd-footer-rule, ${COLORS.strokeFaded});
  background: var(--pd-footer-bg, ${COLORS.white});
  box-shadow: 0 -6px 16px rgba(29, 29, 29, 0.05);
`;

/* Mockup `.po-footer`. The actions the host supplies act on the media in front of the reader, so
   the overlay gives them a place to sit and stays out of what they mean. */
const HOST_FOOTER_STYLE = css`
  label: PHOTO_DETAIL_HOST_FOOTER;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  border-top: 1px solid var(--pd-footer-rule, ${COLORS.strokeFaded});
  background: var(--pd-footer-bg, ${COLORS.white});
`;

const ACTION_BUTTON_STYLE = css`
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid var(--pd-button-border, ${COLORS.stroke});
  border-radius: 0;
  background: var(--pd-button-bg, ${COLORS.white});
  color: var(--pd-pane-text, ${COLORS.ink});
  font: inherit;
  font-size: ${pxToRem(14)};
  font-weight: 700;
  line-height: 1.286;
  white-space: nowrap;
  cursor: pointer;

  svg {
    flex: 0 0 auto;
  }
`;

const DELETE_BUTTON_STYLE = css`
  flex: 0 0 auto;
  min-width: 38px;
  border-color: ${COLORS.error};
  color: ${COLORS.error};
`;

const REQUEST_EDIT_BUTTON_STYLE = css`
  flex: 1 1 0;
  min-width: 124px;
  border-color: ${COLORS.stroke};
  color: ${COLORS.ink};
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const APPROVE_BUTTON_STYLE = css`
  flex: 1.4 1 0;
  min-width: 132px;
  border-color: #1a1a1a;
  background: #1a1a1a;
  color: #fff;
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const DARK_PANE = '#1a1a1a';

const MODAL_STYLE = {
  height: 'min(820px, 92vh)',
  maxWidth: 1240,
  gridTemplateColumns: 'minmax(0, 65fr) minmax(0, 35fr)',
};

/* The photo manager opens this inside a takeover it already owns, so a centred card over its own
   scrim would be a modal on top of a modal. `fill` drops ModalLayout's chrome and lets the panes
   occupy the surface directly — the mockup's `.pm-photo-stage`, which is `inset: 0` inside the
   takeover panel rather than an overlay of its own. */
const FILL_LAYOUT_STYLE = css`
  label: PHOTO_DETAIL_FILL_LAYOUT;
  flex: 1;
  min-height: 0;
  display: grid;
  /* The photo takes 60 of the takeover's width, the rail keeps the 15 it had, and the detail pane
     gives up the difference. The rail is flex:15 against the hero's 60 inside a 75 column, so
     growing the column does not widen it. */
  grid-template-columns: minmax(0, 75fr) minmax(0, 25fr);
  --pd-hero-flex: 60;
  overflow: hidden;
  background: ${DARK_PANE};

  /* Mockup .pm-photo-panes: the pane's light values each get a dark counterpart rather than a
     filter over the whole surface, which would have muddied the filmstrip's photos too. Set here
     rather than on the styles themselves so every other host keeps the light treatment. */
  --pd-pane-bg: ${DARK_PANE};
  --pd-pane-text: #fff;
  --pd-muted: rgba(255, 255, 255, 0.5);
  --pd-rail-title: rgba(255, 255, 255, 0.72);
  --pd-subtitle: rgba(255, 255, 255, 0.72);
  --pd-rule: rgba(255, 255, 255, 0.14);
  --pd-rule-soft: rgba(255, 255, 255, 0.12);
  --pd-hero-bg: #0f0f0f;
  --pd-note-bg: #242424;
  --pd-note-rule: rgba(255, 255, 255, 0.35);
  --pd-footer-bg: #141414;
  --pd-footer-rule: rgba(255, 255, 255, 0.16);
  --pd-button-border: rgba(255, 255, 255, 0.28);
  --pd-button-bg: transparent;
`;

/** Widest a rail tile gets: about 170px in the centered modal, about 265px filling the Photo Manager takeover. */
const RAIL_TILE_MAX_WIDTH_PX = 270;

/** Mockup `.po-strip-title`: "3 photos", but "1 photo". */
const getPhotoCountLabel = (count: number): string => `${count} ${count === 1 ? 'photo' : 'photos'}`;

const getInitialThumbnailId = (model: PhotoDetailModel): string | undefined => {
  const candidates = model.navThumbnails ?? model.thumbnails;
  return candidates?.find(thumbnail => thumbnail.selected)?.id ?? candidates?.[0]?.id;
};

const getThumbnailIndex = (thumbnails: readonly PhotoDetailThumbnailModel[], thumbnailId?: string) =>
  Math.max(thumbnails.findIndex(thumbnail => thumbnail.id === thumbnailId), 0);

const PhotoThumbnailButton = ({
  thumbnail,
  active,
  compact,
  fillWidth,
  onSelect,
}: {
  thumbnail: PhotoDetailThumbnailModel;
  active?: boolean;
  compact?: boolean;
  /** Stretch to the rail column's full width (image keeps a 4:3 ratio). */
  fillWidth?: boolean;
  onSelect?: (id: string) => void;
}) => {
  const captionMatch = thumbnail.captionLabel?.match(/^Photo\s+(.+)$/);
  const imageUrl = usePhotoUriForLayoutSize(thumbnail.photoId, fillWidth ? RAIL_TILE_MAX_WIDTH_PX : compact ? 96 : 178);

  return (
    <button
      type='button'
      aria-label={thumbnail.title}
      onClick={() => onSelect?.(thumbnail.id)}
      style={{
        position: 'relative',
        width: fillWidth ? '100%' : compact ? 96 : 178,
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        padding: 0,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: active ? '${COLORS.paper}' : 'rgba(255, 255, 255, 0.14)',
        borderRadius: compact ? 4 : 8,
        background: '#050505',
        color: '${COLORS.stroke}',
        cursor: onSelect ? 'pointer' : 'default',
        overflow: 'hidden',
        textAlign: 'left',
        font: 'inherit',
      }}
    >
      <span
        style={{
          position: 'relative',
          height: fillWidth ? 'auto' : compact ? 54 : 96,
          aspectRatio: fillWidth ? '4 / 3' : undefined,
          display: 'block',
          borderRadius: compact ? 3 : '6px 6px 0 0',
          backgroundColor: '${COLORS.ink}',
          backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        {!compact && thumbnail.walkLabel
          ? (
            <span
              style={{
                position: 'absolute',
                top: 10,
                left: 10,
                padding: '4px 12px',
                borderRadius: 999,
                background: 'rgba(29, 29, 29, 0.72)',
                color: '#fff',
                fontSize: pxToRem(12),
                fontWeight: 700,
                letterSpacing: '0.01em',
              }}
            >
              {thumbnail.walkLabel}
            </span>
          )
          : null}
      </span>
      {!compact && active
        ? (
          <span
            aria-hidden='true'
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 14,
              height: 14,
              border: '2px solid #fff',
              borderRadius: 999,
              background: '#7a746b',
            }}
          />
        )
        : null}
      {compact ? null : (
        <span style={{ display: 'flex', alignItems: 'center', minHeight: 48, padding: '0 14px', fontSize: pxToRem(15) }}>
          {captionMatch
            ? (
              <>
                <span style={{ color: '#8f8f8f' }}>Photo&nbsp;</span>
                <span style={{ color: '#fff' }}>{captionMatch[1]}</span>
              </>
            )
            : thumbnail.captionLabel}
        </span>
      )}
    </button>
  );
};

const GroupPhotoNavButton = ({ thumbnail, onSelect }: { thumbnail: PhotoDetailThumbnailModel; onSelect: () => void }) => {
  const imageUrl = usePhotoUriForLayoutSize(thumbnail.photoId, RAIL_TILE_MAX_WIDTH_PX);

  return (
    <button
      type='button'
      css={GROUP_PHOTO_NAV_STYLE}
      aria-label={`Go to ${thumbnail.taskLabel ?? 'task'}`}
      title={thumbnail.taskLabel ?? 'Go to task'}
      onClick={onSelect}
    >
      <span
        aria-hidden='true'
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          borderRadius: 4,
          backgroundColor: '#908062',
          backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        } as CSSProperties}
      />
    </button>
  );
};

const MediaPane = ({
  model,
  selectedThumbnailId,
  onSelectThumbnail,
  onSelectTimelinePhoto,
}: {
  model: PhotoDetailModel;
  selectedThumbnailId?: string;
  onSelectThumbnail: (id: string | undefined) => void;
  onSelectTimelinePhoto?: (cellId: string, taskId: string) => void;
}) => {
  const thumbnails = model.thumbnails ?? [];
  /* The arrows and the caption's count belong to everything on offer; the rail below them may be
     scoped to less than that, so the two are read from separate lists. */
  const navThumbnails = model.navThumbnails ?? thumbnails;
  const activeIndex = getThumbnailIndex(navThumbnails, selectedThumbnailId);
  const activeThumbnail = navThumbnails[activeIndex];
  const activeCountLabel = navThumbnails.length ? `${activeIndex + 1} of ${navThumbnails.length}` : undefined;
  const activeMetaLabel = activeThumbnail?.metaLabel ?? [activeCountLabel, activeThumbnail?.title].filter(Boolean).join(' · ');
  const heroImageUrl = usePhotoFullSizeUri(activeThumbnail?.photoId ?? model.heroPhotoId);

  const selectByOffset = (offset: number) => {
    const nextThumbnail = navThumbnails[activeIndex + offset];
    if (nextThumbnail) onSelectThumbnail(nextThumbnail.id);
  };

  return (
    <div css={MEDIA_ROW_STYLE}>
      <div css={PHOTO_MEDIA_STYLE}>
        <div css={HERO_WRAP_STYLE}>
          <div
            css={HERO_STYLE}
            data-closeout-photo-detail-hero='true'
            style={{ backgroundImage: heroImageUrl ? `url(${heroImageUrl})` : undefined }}
          />
          <button
            type='button'
            css={[PHOTO_NAV_BUTTON_STYLE, PHOTO_NAV_PREVIOUS_STYLE]}
            aria-label='Previous photo'
            disabled={activeIndex <= 0}
            onClick={() => selectByOffset(-1)}
          >
            <LeftIcon size={20} aria-hidden='true' data-closeout-detail-icon='true' />
          </button>
          <button
            type='button'
            css={[PHOTO_NAV_BUTTON_STYLE, PHOTO_NAV_NEXT_STYLE]}
            aria-label='Next photo'
            disabled={activeIndex >= navThumbnails.length - 1}
            onClick={() => selectByOffset(1)}
          >
            <RightIcon size={20} aria-hidden='true' data-closeout-detail-icon='true' />
          </button>
        </div>

        <div css={CAPTION_BAR_STYLE} data-testid='photo-detail-caption'>
          {activeThumbnail?.metaLabel ?? activeMetaLabel}
        </div>
      </div>

      <div css={RAIL_STYLE} data-testid='photo-detail-rail'>
        <section css={RAIL_SECTION_STYLE}>
          <h3 css={RAIL_TITLE_STYLE}>{`${model.title} · ${getPhotoCountLabel(thumbnails.length)}`}</h3>
          <div css={RAIL_COLUMN_STYLE}>
            {thumbnails.map(thumbnail => (
              <PhotoThumbnailButton
                key={thumbnail.id}
                thumbnail={thumbnail}
                compact
                fillWidth
                active={thumbnail.id === activeThumbnail?.id}
                onSelect={onSelectThumbnail}
              />
            ))}
          </div>
        </section>

        {model.groupPhotos?.length
          ? (
            <section css={RAIL_SECTION_STYLE}>
              <h3 css={RAIL_TITLE_STYLE}>{`Group · ${getPhotoCountLabel(model.groupPhotos.length)}`}</h3>
              <div css={RAIL_COLUMN_STYLE}>
                {model.groupPhotos.map(thumbnail => (
                  thumbnail.navCellId && thumbnail.taskId && onSelectTimelinePhoto
                    ? (
                      <GroupPhotoNavButton
                        key={thumbnail.id}
                        thumbnail={thumbnail}
                        onSelect={() => onSelectTimelinePhoto(thumbnail.navCellId!, thumbnail.taskId!)}
                      />
                    )
                    : (
                      <PhotoThumbnailButton
                        key={thumbnail.id}
                        thumbnail={thumbnail}
                        compact
                        fillWidth
                      />
                    )
                ))}
              </div>
            </section>
          )
          : null}
      </div>
    </div>
  );
};

const PHOTO_INFO_LABELS = ['Status', 'Walk date', 'Field user', 'Source'] as const;

const DetailPane = ({
  model,
  onApproveTask,
  onClose,
  presentation,
  footer,
  onDeleteTask,
  onRequestTaskEdit,
  onGoToEditor,
}: {
  model: PhotoDetailModel;
  onApproveTask?: (taskId: string) => void | Promise<void>;
  onClose: () => void;
  presentation?: 'modal' | 'fill';
  footer?: ReactNode;
  onDeleteTask?: (taskId: string) => void | Promise<void>;
  onRequestTaskEdit?: (taskId: string) => void | Promise<void>;
  onGoToEditor?: (taskId: string) => void;
}) => {
  const taskId = model.taskId;
  const taskTotal = model.financials?.find(row => row.label === 'Task total');
  const photoInfoRows = model.photoInfo
    ?? PHOTO_INFO_LABELS.map(label => model.taskInfo?.find(row => row.label === label) ?? { label });
  const taskIdRow = model.taskInfo?.find(row => row.label === 'Task ID');
  const contractorRow = model.contractor?.find(row => row.label === 'Name')?.value;
  const contractorName = contractorRow && contractorRow !== '—' ? contractorRow : undefined;
  const laborValue = model.financials?.find(row => row.label === 'Labor')?.value;
  const taskInfoRows = [
    { label: 'Task ID', value: taskIdRow?.value },
    { label: 'Contractor', value: contractorName ?? 'Unassigned' },
    { label: 'Labor', hint: model.laborBasis, value: laborValue },
    ...(model.materials ?? []).map(material => ({ label: material.name, value: material.valueLabel })),
  ];

  // Each action belongs to the photo it was taken on, so moving to another photo clears it.
  const [approvedModelId, setApprovedModelId] = useState<string>();
  const [editRequestedModelId, setEditRequestedModelId] = useState<string>();
  const approved = approvedModelId === model.id;
  const editRequested = editRequestedModelId === model.id;

  return (
    <div css={DETAIL_PANEL_STYLE}>
      <div css={DETAIL_SCROLL_STYLE} data-testid='photo-detail-scroll-region'>
        <header css={DETAIL_MOCKUP_HEADER_STYLE}>
          <div css={DETAIL_TITLE_WRAP_STYLE}>
            <h2 css={DETAIL_TITLE_STYLE}>{model.title}</h2>
            {(model.group?.title ?? model.subtitle) ? <div css={DETAIL_SUBTITLE_STYLE}>{model.group?.title ?? model.subtitle}</div> : null}
          </div>
        </header>

        <div css={TASK_TOTAL_ROW_STYLE} data-testid='photo-detail-task-total'>
          <span css={TASK_TOTAL_LABEL_STYLE}>Task total</span>
          <span css={TASK_TOTAL_VALUE_STYLE}>{taskTotal?.value ?? '—'}</span>
          {onGoToEditor && taskId
            ? (
              <button type='button' css={GO_TO_EDITOR_STYLE} onClick={() => onGoToEditor(taskId)}>
                Go to Editor →
              </button>
            )
            : null}
        </div>

        <DetailSection title='PHOTO INFO' rows={photoInfoRows} />
        <DetailSection title='TASK INFO' rows={taskInfoRows} />

        {model.description
          ? (
            <section css={NOTES_SECTION_STYLE} data-testid='closeout-photo-detail-section-notes'>
              <h3 css={SECTION_TITLE_STYLE}>NOTES</h3>
              <div css={QUOTE_CARD_STYLE}>
                <p css={QUOTE_TEXT_STYLE}>{model.description}</p>
                <span css={QUOTE_CAPTION_STYLE}>TASK DESCRIPTION</span>
              </div>
            </section>
          )
          : null}
      </div>

      {
        /* The takeover hosting the fill variant closes from its own header — mockup `_pmPhotoModeHtml`
          renders the pane with no close of its own, so a second one here would be two ways out
          sitting one above the other. */
      }
      {footer ? <footer css={HOST_FOOTER_STYLE} data-testid='photo-detail-host-footer'>{footer}</footer> : null}

      {presentation === 'fill' ? null : (
        <IconButton
          aria-label='Close photo detail'
          onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, borderRadius: 999, background: COLORS.paper, boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}
        >
          <CloseIcon size={16} aria-hidden='true' data-closeout-detail-icon='true' />
        </IconButton>
      )}

      {
        /* Task review actions. A host with none of them — the Photo Manager takeover, which acts on
          photos rather than on the task — gets no action row at all rather than a row of dead
          buttons. Delete stays rendered-but-disabled when only its own handler is missing. */
      }
      {onApproveTask || onRequestTaskEdit || onDeleteTask
        ? (
          <footer css={ACTION_ROW_STYLE} data-testid='closeout-photo-detail-actions' style={{ position: 'sticky', bottom: 0, zIndex: 1 }}>
            <button
              type='button'
              css={[ACTION_BUTTON_STYLE, DELETE_BUTTON_STYLE]}
              aria-label='Delete task'
              disabled={!taskId || !onDeleteTask}
              title='Delete task'
              onClick={() => taskId && onDeleteTask?.(taskId)}
            >
              <TrashIcon size={16} color='currentColor' aria-hidden='true' />
            </button>
            {onRequestTaskEdit && (
              <button
                type='button'
                css={[ACTION_BUTTON_STYLE, REQUEST_EDIT_BUTTON_STYLE]}
                aria-label={editRequested ? 'Edit requested' : 'Request edit'}
                disabled={!taskId || editRequested}
                onClick={async () => {
                  if (!taskId) return;
                  await onRequestTaskEdit(taskId);
                  setEditRequestedModelId(model.id);
                }}
              >
                <EditIcon size={16} color='currentColor' aria-hidden='true' />
                <span>{editRequested ? 'Edit requested' : 'Request edit'}</span>
              </button>
            )}
            {onApproveTask && (
              <button
                type='button'
                css={[ACTION_BUTTON_STYLE, APPROVE_BUTTON_STYLE]}
                aria-label={approved ? 'Approved' : 'Approve task'}
                disabled={!taskId || approved}
                onClick={async () => {
                  if (!taskId) return;
                  await onApproveTask(taskId);
                  setApprovedModelId(model.id);
                }}
              >
                <CheckIcon size={16} color='currentColor' aria-hidden='true' />
                <span>{approved ? 'Approved' : 'Approve task'}</span>
              </button>
            )}
          </footer>
        )
        : null}
    </div>
  );
};

export const PhotoDetailModal = ({
  model,
  onApproveTask,
  onClose,
  onDeleteTask,
  onRequestTaskEdit,
  onSelectTimelinePhoto,
  onGoToEditor,
  onActiveThumbnailChange,
  presentation = 'modal',
  footer,
}: {
  model: PhotoDetailModel;
  onApproveTask?: (taskId: string) => void | Promise<void>;
  onClose: () => void;
  onDeleteTask?: (taskId: string) => void | Promise<void>;
  onRequestTaskEdit?: (taskId: string) => void | Promise<void>;
  onSelectTimelinePhoto?: (cellId: string, taskId: string) => void;
  onGoToEditor?: (taskId: string) => void;
  /**
   * Fired with the thumbnail the hero is currently showing, on open and on every step through the
   * strip. The modal owns the selection either way; this only lets a host that wants to — the Photo
   * Manager, resolving the hero at full size — follow along without taking the selection over.
   */
  onActiveThumbnailChange?: (thumbnailId: string | undefined) => void;
  /** `fill` for a host that already owns a takeover surface; see FILL_LAYOUT_STYLE. */
  presentation?: 'modal' | 'fill';
  /** The host's own actions on this photo, shown in the pane's footer. */
  footer?: ReactNode;
}) => {
  const [selectedThumbnailId, setSelectedThumbnailId] = useState(() => getInitialThumbnailId(model));

  useEffect(() => {
    setSelectedThumbnailId(getInitialThumbnailId(model));
  }, [model]);

  useEffect(() => {
    onActiveThumbnailChange?.(selectedThumbnailId);
  }, [onActiveThumbnailChange, selectedThumbnailId]);

  const mediaPane = (
    <MediaPane
      model={model}
      selectedThumbnailId={selectedThumbnailId}
      onSelectThumbnail={setSelectedThumbnailId}
      onSelectTimelinePhoto={onSelectTimelinePhoto}
    />
  );
  const detailPane = (
    <DetailPane
      model={model}
      onApproveTask={onApproveTask}
      onClose={onClose}
      onDeleteTask={onDeleteTask}
      onRequestTaskEdit={onRequestTaskEdit}
      onGoToEditor={onGoToEditor}
      presentation={presentation}
      footer={footer}
    />
  );

  if (presentation === 'fill' && !model.missingPhoto) {
    return (
      <div
        aria-label='Photo detail'
        css={FILL_LAYOUT_STYLE}
        data-review-flow-photo-detail-modal='true'
        role='dialog'
      >
        {mediaPane}
        {detailPane}
      </div>
    );
  }

  if (!model.missingPhoto) {
    return (
      <ModalLayout
        role='dialog'
        aria-modal='true'
        aria-label='Closeout photo detail'
        data-review-flow-photo-detail-modal='true'
        style={MODAL_STYLE}
        mediaPane={
          <MediaPane
            model={model}
            selectedThumbnailId={selectedThumbnailId}
            onSelectThumbnail={setSelectedThumbnailId}
            onSelectTimelinePhoto={onSelectTimelinePhoto}
          />
        }
        detailPane={
          <DetailPane
            model={model}
            onApproveTask={onApproveTask}
            onClose={onClose}
            onDeleteTask={onDeleteTask}
            onRequestTaskEdit={onRequestTaskEdit}
            onGoToEditor={onGoToEditor}
          />
        }
      />
    );
  }

  return (
    <ModalLayout
      role='dialog'
      aria-modal='true'
      aria-label='Missing closeout photo'
      data-closeout-missing-photo-modal='true'
      style={MODAL_STYLE}
      mediaPane={
        <div css={MISSING_MEDIA_STYLE}>
          <div css={MISSING_CARD_STYLE}>
            <span css={MISSING_ICON_STYLE} aria-hidden='true'>
              <MissingPhotoIcon size={24} color='#a96947' />
            </span>
            <span css={MISSING_EYEBROW_STYLE}>No photo</span>
            <strong css={MISSING_TITLE_STYLE}>Photo missing for this evidence slot</strong>
          </div>
        </div>
      }
      detailPane={
        <div css={DETAIL_STYLE}>
          <div css={DETAIL_HEADER_STYLE}>
            <div css={DETAIL_TITLE_WRAP_STYLE}>
              <span css={WARNING_BADGE_STYLE}>Missing evidence</span>
              <h2 css={DETAIL_TITLE_STYLE}>{model.title}</h2>
              {model.subtitle ? <div css={DETAIL_SUBTITLE_STYLE}>{model.subtitle}</div> : null}
            </div>
            <IconButton aria-label='Close photo detail' onClick={onClose}>
              <CloseIcon size={16} aria-hidden='true' data-closeout-detail-icon='true' />
            </IconButton>
          </div>

          <section css={SECTION_STYLE}>
            <span css={SECTION_TITLE_STYLE}>Evidence gap</span>
            <div css={BODY_TEXT_STYLE}>{model.notes}</div>
          </section>

          <section css={SECTION_STYLE}>
            <span css={SECTION_TITLE_STYLE}>Suggested next step</span>
            <div css={BODY_TEXT_STYLE}>
              Review the adjacent evidence tiles or capture a closeout photo in Kai Pro before making a final decision.
            </div>
          </section>
        </div>
      }
    />
  );
};
